import React, { useMemo, useRef, useState } from 'react';
import { Building2, Crosshair, Loader2, MapPin, Plus, Trash2, Wifi, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LeafletMap } from '@/components/transport/LeafletMap';
import { getGPS, transportApi } from '@/services/transportApi';

const hasPin = (l) => l.lat !== '' && l.lng !== '' && l.lat != null && l.lng != null;

/** Optional office Wi-Fi (public IPs) for one work location — punches from them are On-site without GPS. */
const LocationWifi = ({ value, onChange, i }) => {
  const [ip, setIp] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = async () => {
    setBusy(true);
    try { const r = await transportApi.myIp(); if (r.ip) setIp(r.ip); else toast.error('Could not detect your IP'); }
    catch { toast.error('Could not detect your IP'); } finally { setBusy(false); }
  };
  const add = () => {
    const v = ip.trim();
    if (!v) { toast.error('Enter an IP address'); return; }
    if (value.some((o) => o.ip === v)) { toast.error('That IP is already saved'); return; }
    onChange([...value, { ip: v, name: name.trim() }]);
    setIp(''); setName('');
  };
  return (
    <div className="space-y-1.5" data-testid={`loc-wifi-${i}`}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((o) => (
          <span key={o.ip} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs" data-testid={`loc-wifi-chip-${i}-${o.ip}`}>
            <Building2 className="w-3 h-3 text-emerald-600" />{o.name || 'Wi-Fi'} <span className="font-mono text-gray-500">{o.ip}</span>
            <button type="button" onClick={() => onChange(value.filter((x) => x.ip !== o.ip))} className="text-gray-400 hover:text-red-500" data-testid={`loc-wifi-remove-${i}-${o.ip}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Wifi className="w-4 h-4 text-emerald-600" />
        <Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="Wi-Fi public IP (optional)" className="h-8 w-[190px] font-mono text-xs" data-testid={`loc-wifi-ip-${i}`} />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Wi-Fi name" onKeyDown={(e) => e.key === 'Enter' && add()} className="h-8 w-[150px] text-xs" data-testid={`loc-wifi-name-${i}`} />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={mine} disabled={busy} data-testid={`loc-wifi-myip-${i}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'My IP'}</Button>
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={add} data-testid={`loc-wifi-add-${i}`}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
};

const LocationRow = ({ loc, i, active, onSelect, onChange, onRemove }) => {
  const [busy, setBusy] = useState(false);
  const here = async (e) => {
    e.stopPropagation();
    setBusy(true);
    const g = await getGPS(10000);
    setBusy(false);
    if (!g) { toast.error('Could not get your location — turn on GPS'); return; }
    onChange({ ...loc, lat: +g.lat.toFixed(6), lng: +g.lng.toFixed(6) });
  };
  const f = (k) => (e) => onChange({ ...loc, [k]: e.target.value });
  return (
    <div onClick={onSelect} className={`rounded-xl border p-3 space-y-2 cursor-pointer transition-colors ${active ? 'border-amber-400 bg-amber-50/60' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`loc-row-${i}`}>
      <div className="flex flex-wrap items-end gap-2">
        <MapPin className={`w-5 h-5 mb-2 ${active ? 'text-amber-500' : 'text-slate-500'}`} />
        <div><label className="text-[11px] text-gray-500">Name</label><Input value={loc.name} onChange={f('name')} className="w-[200px] bg-white" data-testid={`loc-name-${i}`} /></div>
        <div><label className="text-[11px] text-gray-500">Latitude</label><Input value={loc.lat} onChange={f('lat')} className="w-[120px] font-mono bg-white" data-testid={`loc-lat-${i}`} /></div>
        <div><label className="text-[11px] text-gray-500">Longitude</label><Input value={loc.lng} onChange={f('lng')} className="w-[120px] font-mono bg-white" data-testid={`loc-lng-${i}`} /></div>
        <div><label className="text-[11px] text-gray-500">Radius (m)</label><Input type="number" value={loc.radius_m} onChange={f('radius_m')} className="w-[90px] bg-white" data-testid={`loc-radius-${i}`} /></div>
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={here} disabled={busy} data-testid={`loc-here-${i}`}>{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}Use my location</Button>
        <Button type="button" variant="ghost" size="icon" className="text-red-600 ml-auto" onClick={(e) => { e.stopPropagation(); onRemove(); }} data-testid={`loc-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
      <LocationWifi value={loc.networks || []} onChange={(n) => onChange({ ...loc, networks: n })} i={i} />
    </div>
  );
};

/** HR: any number of work locations (map pin + radius + optional Wi-Fi). Inside ANY of them = On-site. */
export const WorkLocations = ({ value, onChange }) => {
  const [sel, setSel] = useState(0);
  const [touched, setTouched] = useState(false);
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const set = (i, v) => { setTouched(true); latest.current.onChange(latest.current.value.map((x, j) => (j === i ? v : x))); };
  const add = () => { onChange([...value, { id: '', name: `Location ${value.length + 1}`, lat: '', lng: '', radius_m: 300, networks: [] }]); setSel(value.length); };
  const remove = (i) => { onChange(value.filter((_, j) => j !== i)); setSel(0); };
  const key = JSON.stringify(value.map((l) => [l.lat, l.lng, l.radius_m, l.name])) + sel;
  const pins = useMemo(() => value.map((l, i) => (hasPin(l) ? { i, l } : null)).filter(Boolean), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const markers = useMemo(() => pins.map(({ i, l }) => ({
    lat: Number(l.lat), lng: Number(l.lng), color: i === sel ? '#F59E0B' : '#1A1F2E', label: l.name, draggable: true,
    onDragEnd: (lat, lng) => set(i, { ...latest.current.value[i], lat: +lat.toFixed(6), lng: +lng.toFixed(6) }),
  })), [pins]); // eslint-disable-line react-hooks/exhaustive-deps
  const circles = useMemo(() => pins.map(({ i, l }) => ({ lat: Number(l.lat), lng: Number(l.lng), radius: Number(l.radius_m) || 300, color: i === sel ? '#F59E0B' : '#475569' })), [pins]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (lat, lng) => { const cur = latest.current.value[sel]; if (cur) set(sel, { ...cur, lat: +lat.toFixed(6), lng: +lng.toFixed(6) }); };

  return (
    <Card className="border-0 shadow-md" data-testid="work-locations"><CardContent className="p-5 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold">Work locations</h3>
          <p className="text-sm text-gray-500">A punch inside ANY location's circle (or on its Wi-Fi) is <b>On-site</b> and shows that location's name. Anywhere else is <b>Off-site</b> and always needs a manual approval.</p>
        </div>
        <Button type="button" variant="outline" onClick={add} className="border-amber-400 text-amber-700" data-testid="loc-add-btn"><Plus className="w-4 h-4 mr-1" />Add location</Button>
      </div>
      <LeafletMap center={markers[0] ? [markers[0].lat, markers[0].lng] : [11.6643, 78.146]} zoom={12} markers={markers} circles={circles}
        onMapClick={pick} fit={!touched} height="300px" testId="work-locations-map" />
      <p className="text-xs text-gray-500">Click a location below, then click the map (or drag its pin) to place it.</p>
      {value.length === 0 && <p className="text-sm text-gray-400" data-testid="loc-empty">No work locations yet — every punch will be Off-site until you add one.</p>}
      <div className="space-y-2">
        {value.map((l, i) => <LocationRow key={l.id || `new-${i}`} loc={l} i={i} active={i === sel} onSelect={() => setSel(i)} onChange={(v) => set(i, v)} onRemove={() => remove(i)} />)}
      </div>
    </CardContent></Card>
  );
};

export default WorkLocations;
